export type PyAnalystEndpointSpec = {
  method?: string;
  headers?: Record<string, string>;
  function_url?: string;
  sample_payload?: Record<string, unknown>;
};

export type PyAnalystEndpointConfig = PyAnalystEndpointSpec & {
  key: string;
  /** Set on create when date values are stripped from sample_payload. */
  date_filter_column?: string;
};

export type PyAnalystReportItem = {
  analyst_id?: string;
  s3_key?: string;
};

export type PyAnalystReportResponse = {
  status_code?: number;
  message?: string;
  data?: PyAnalystReportItem[];
};

export const PYANALYST_ENDPOINT_ATTR = 'pyanalyst_endpoint';
export const PYANALYST_ENDPOINT_CONFIG_ATTR = 'pyanalyst_endpoint_config';

export function parsePyAnalystEndpointConfig(raw: unknown): PyAnalystEndpointConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const config = raw as PyAnalystEndpointConfig;
  if (!config.key || !config.function_url) return null;
  return config;
}

export function buildEndpointConfigFromSelection(
  key: string,
  spec: PyAnalystEndpointSpec
): PyAnalystEndpointConfig {
  return {
    key,
    method: spec.method ?? 'POST',
    headers: spec.headers ?? {},
    function_url: spec.function_url ?? '',
    sample_payload: spec.sample_payload ?? {},
  };
}

export type PyAnalystDateFilter = {
  column: string;
  from: string;
  to: string;
};

export function extractDateFiltersFromPayload(samplePayload?: Record<string, unknown>): PyAnalystDateFilter[] {
  const filters = samplePayload?.filters;
  if (!Array.isArray(filters)) return [];

  return filters
    .map((entry) => {
      if (!entry || typeof entry !== 'object') return null;
      const filter = entry as { column?: string; value?: { from?: string; to?: string } };
      const from = filter.value?.from;
      const to = filter.value?.to;
      if (!filter.column || !from || !to) return null;
      return { column: filter.column, from, to };
    })
    .filter((item): item is PyAnalystDateFilter => item !== null);
}

export function applyDateFiltersToPayload(
  samplePayload: Record<string, unknown>,
  dateFilters: PyAnalystDateFilter[]
): Record<string, unknown> {
  const payload = JSON.parse(JSON.stringify(samplePayload)) as Record<string, unknown>;
  const filters = payload.filters;
  if (!Array.isArray(filters)) return payload;

  dateFilters.forEach(({ column, from, to }) => {
    let match = filters.find(
      (entry) => entry && typeof entry === 'object' && (entry as { column?: string }).column === column
    ) as { column?: string; value?: Record<string, string> } | undefined;
    if (!match) {
      match = { column, value: { from, to } };
      filters.push(match);
      return;
    }
    match.value = { from, to };
  });

  return payload;
}

export function stripDateRangeFiltersFromPayload(
  samplePayload: Record<string, unknown>
): Record<string, unknown> {
  const payload = JSON.parse(JSON.stringify(samplePayload)) as Record<string, unknown>;
  const filters = payload.filters;
  if (!Array.isArray(filters)) return payload;

  payload.filters = filters.filter((entry) => {
    if (!entry || typeof entry !== 'object') return true;
    const value = (entry as { value?: { from?: string; to?: string } }).value;
    return !(value && typeof value === 'object' && value.from && value.to);
  });
  return payload;
}

export function resolvePyAnalystDateColumn(config: PyAnalystEndpointConfig | null): string | null {
  if (!config) return null;
  if (config.date_filter_column) return config.date_filter_column;

  const dated = extractDateFiltersFromPayload(config.sample_payload)[0]?.column;
  if (dated) return dated;

  const filters = config.sample_payload?.filters;
  if (!Array.isArray(filters)) return null;
  for (const entry of filters) {
    if (entry && typeof entry === 'object' && (entry as { column?: string }).column) {
      return (entry as { column: string }).column;
    }
  }
  return null;
}

export function preparePyAnalystEndpointConfigForSave(
  key: string,
  spec: PyAnalystEndpointSpec
): PyAnalystEndpointConfig {
  const templatePayload = (spec.sample_payload ?? {}) as Record<string, unknown>;
  const dateColumn = extractDateFiltersFromPayload(templatePayload)[0]?.column;
  const endpointConfig = buildEndpointConfigFromSelection(key, spec);
  endpointConfig.sample_payload = stripDateRangeFiltersFromPayload(
    (endpointConfig.sample_payload ?? {}) as Record<string, unknown>
  );
  if (dateColumn) {
    endpointConfig.date_filter_column = dateColumn;
  }
  return endpointConfig;
}

export function resolvePyAnalystHeaders(
  headers: Record<string, string> | undefined,
  token: string
): Record<string, string> {
  const resolved: Record<string, string> = {};
  Object.entries(headers ?? {}).forEach(([name, value]) => {
    resolved[name] = String(value).replace(/\$\{Authorization\}/g, `Bearer ${token}`);
  });
  return resolved;
}

type AiWorkerWithAttrs = {
  aiworkers_attrs?: Array<{ attr_name?: string; attr_json?: unknown }>;
};

export function getPyAnalystConfigFromAiWorker(
  aiworker: AiWorkerWithAttrs | null | undefined
): PyAnalystEndpointConfig | null {
  const configAttr = aiworker?.aiworkers_attrs?.find(
    (attr) => attr.attr_name === PYANALYST_ENDPOINT_CONFIG_ATTR
  );
  return parsePyAnalystEndpointConfig(configAttr?.attr_json);
}

type PresignedUrlFetcher = (body: {
  evaluation_id: string;
  s3_key: string;
}) => Promise<{ data?: { presigned_url?: string } }>;

/** Calls ML function_url then presigned-url API; returns iframe URL. */
export async function fetchPyAnalystPresignedReportUrl(
  config: PyAnalystEndpointConfig,
  dateColumn: string,
  from: string,
  to: string,
  token: string,
  getPresignedUrl: PresignedUrlFetcher
): Promise<string> {
  const basePayload = (config.sample_payload ?? {}) as Record<string, unknown>;
  const requestPayload = applyDateFiltersToPayload(basePayload, [{ column: dateColumn, from, to }]);

  const method = (config.method ?? 'POST').toUpperCase();
  const reportResponse = await fetch(config.function_url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...resolvePyAnalystHeaders(config.headers, token),
    },
    ...(method !== 'GET' ? { body: JSON.stringify(requestPayload) } : {}),
  });

  if (!reportResponse.ok) {
    throw new Error('Unable to fetch PyAnalyst report');
  }

  const reportPayload = (await reportResponse.json()) as PyAnalystReportResponse;
  const firstItem = reportPayload.data?.[0];
  const evaluationId = firstItem?.analyst_id;
  const s3Key = firstItem?.s3_key;

  if (!evaluationId || !s3Key) {
    throw new Error(reportPayload.message || 'PyAnalyst response is invalid');
  }

  const presignedPayload = await getPresignedUrl({
    evaluation_id: evaluationId,
    s3_key: s3Key,
  });
  const generatedUrl = presignedPayload?.data?.presigned_url;
  if (!generatedUrl) {
    throw new Error('Presigned URL is missing in response');
  }
  return generatedUrl;
}
